import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Glasses, Eye, Calendar, ExternalLink, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { SlotPicker } from './SlotPicker';
import { doctolibRenewalByAge } from '../doctolibLinks';
import './AppointmentModal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AppointmentModal = ({ open, onOpenChange, prefillData }) => {
  const [step, setStep] = useState(1); // 1: Form, 2: Validity Check, 3: Slot Selection, 4: Appointment Type
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    birthDate: '',
    prescriptionDate: '',
  });
  const [validityResult, setValidityResult] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [email, setEmail] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Pre-fill form data when prefillData is provided
  useEffect(() => {
    if (prefillData && open) {
      setFormData(prev => ({
        ...prev,
        birthDate: prefillData.birthDate || '',
        prescriptionDate: prefillData.prescriptionDate || '',
      }));
    }
  }, [prefillData, open]);

  // Helper function to format date input (DD/MM/YYYY)
  const formatDateInput = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  };

  // Helper function to convert DD/MM/YYYY to Date object
  const parseFrenchDate = (dateStr) => {
    if (!dateStr || dateStr.length < 10) return null;
    const [day, month, year] = dateStr.split('/');
    if (!day || !month || !year) return null;
    return new Date(year, month - 1, day);
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for backend
  const convertToISODate = (dateStr) => {
    if (!dateStr || dateStr.length < 10) return '';
    const [day, month, year] = dateStr.split('/');
    if (!day || !month || !year) return '';
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format dates automatically
    if (name === 'birthDate' || name === 'prescriptionDate') {
      setFormData({
        ...formData,
        [name]: formatDateInput(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const calculateValidity = () => {
    const birth = parseFrenchDate(formData.birthDate);
    const prescription = parseFrenchDate(formData.prescriptionDate);
    
    if (!birth || !prescription) {
      return {
        isValid: false,
        expiryDate: '',
        daysRemaining: 0,
      };
    }

    const today = new Date();

    const currentAge = Math.floor((today - birth) / (365.25 * 24 * 60 * 60 * 1000));

    let validityYears;
    if (currentAge < 16) {
      validityYears = 1;
    } else if (currentAge >= 16 && currentAge <= 42) {
      validityYears = 5;
    } else {
      validityYears = 3;
    }

    const expiryDate = new Date(prescription);
    expiryDate.setFullYear(expiryDate.getFullYear() + validityYears);

    const isValid = today <= expiryDate;

    return {
      isValid,
      expiryDate: expiryDate.toLocaleDateString('fr-FR'),
      daysRemaining: Math.floor((expiryDate - today) / (24 * 60 * 60 * 1000)),
    };
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.firstName || !formData.lastName || !formData.phone || !formData.birthDate || !formData.prescriptionDate) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    // Validate date format
    if (formData.birthDate.length < 10 || formData.prescriptionDate.length < 10) {
      alert('Veuillez entrer les dates au format jj/mm/aaaa');
      return;
    }

    // Validate dates are not in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const birthDate = parseFrenchDate(formData.birthDate);
    const prescriptionDate = parseFrenchDate(formData.prescriptionDate);
    
    if (!birthDate || !prescriptionDate) {
      alert('Format de date invalide. Utilisez jj/mm/aaaa');
      return;
    }
    
    if (birthDate > today) {
      alert('La date de naissance ne peut pas être dans le futur');
      return;
    }
    
    if (prescriptionDate > today) {
      alert("La date de l'ordonnance ne peut pas être dans le futur");
      return;
    }

    const validity = calculateValidity();
    setValidityResult(validity);
    setStep(2);
  };

  const handleContinueToSlotSelection = () => {
    if (!validityResult.isValid) {
      // Si ordonnance expirée, rediriger vers Doctolib
      return;
    }
    setStep(3);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleContinueToAppointmentType = () => {
    if (!selectedSlot) {
      alert('Veuillez sélectionner un créneau horaire');
      return;
    }
    setStep(4);
  };

  const handleAppointmentTypeSelection = async (type) => {
    setSelectedType(type);
    setLoading(true);

    try {
      const appointmentData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: email || undefined,
        birthDate: convertToISODate(formData.birthDate),
        prescriptionDate: convertToISODate(formData.prescriptionDate),
        appointmentType: type,
        prescriptionValid: validityResult.isValid,
        appointmentDateTime: selectedSlot ? selectedSlot.start : undefined,
      };

      await axios.post(`${API}/appointments`, appointmentData);
      
      setStep(5);
    } catch (error) {
      console.error('Error creating appointment:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      birthDate: '',
      prescriptionDate: '',
    });
    setValidityResult(null);
    setSelectedType('');
    setSelectedSlot(null);
    setEmail('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="modal-flexible">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6 text-rust" />
            Prise de Rendez-vous
          </DialogTitle>
          <DialogDescription>
            Remplissez le formulaire pour prendre rendez-vous à la Lunetterie du Russey
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Form */}
        {step === 1 && (
          <form onSubmit={handleSubmitForm} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Prénom *</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Jean"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Nom *</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Dupont"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="06 12 34 56 78"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="birthDate">Date de naissance *</Label>
                <Input
                  id="birthDate"
                  name="birthDate"
                  type="text"
                  value={formData.birthDate}
                  onChange={handleInputChange}
                  required
                  placeholder="jj/mm/aaaa"
                  inputMode="numeric"
                  maxLength="10"
                  pattern="\d{2}/\d{2}/\d{4}"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prescriptionDate">Date de l'ordonnance *</Label>
                <Input
                  id="prescriptionDate"
                  name="prescriptionDate"
                  type="text"
                  value={formData.prescriptionDate}
                  onChange={handleInputChange}
                  required
                  placeholder="jj/mm/aaaa"
                  inputMode="numeric"
                  maxLength="10"
                  pattern="\d{2}/\d{2}/\d{4}"
                />
              </div>
            </div>

            {/* Case consentement politique de confidentialité */}
            <div className="privacy-consent">
              <label className="privacy-consent-label">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="privacy-checkbox"
                  required
                />
                <span className="privacy-text">
                  J'accepte la{' '}
                  <a href="/politique-de-confidentialite" target="_blank" rel="noopener noreferrer">
                    politique de confidentialité
                  </a>
                  {' '}*
                </span>
              </label>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-rust hover:bg-rust-light" 
              size="lg"
              disabled={!privacyAccepted}
            >
              Vérifier et Continuer
            </Button>
          </form>
        )}

        {/* Step 2: Validity Check & Appointment Type Selection */}
        {step === 2 && validityResult && (
          <div className="space-y-6 mt-4">
            {/* Validity Result */}
            <Alert className={validityResult.isValid ? 'border-forest bg-forest/5' : 'border-rust bg-rust/5'}>
              <div className="flex items-start gap-3">
                {validityResult.isValid ? (
                  <CheckCircle2 className="h-6 w-6 text-forest mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="h-6 w-6 text-rust mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-lg mb-1 break-words">
                    {validityResult.isValid ? 'Ordonnance Valide ✓' : 'Ordonnance Expirée'}
                  </h4>
                  <AlertDescription className="text-sm break-words">
                    {validityResult.isValid ? (
                      <>
                        Votre ordonnance est valide encore <strong>{validityResult.daysRemaining} jours</strong>
                        <br />
                        (expire le <strong>{validityResult.expiryDate}</strong>).
                      </>
                    ) : (
                      <>
                        Votre ordonnance a expiré le <strong>{validityResult.expiryDate}</strong>.
                        <br />
                        Vous devez consulter un ophtalmologue avant de choisir de nouvelles lunettes.
                      </>
                    )}
                  </AlertDescription>
                </div>
              </div>
            </Alert>

            {/* Valid Prescription - Continue to Slot Selection */}
            {validityResult.isValid && (
              <Button 
                onClick={handleContinueToSlotSelection} 
                className="w-full bg-forest hover:bg-forest-light text-white"
                size="lg"
              >
                Continuer - Choisir un créneau
              </Button>
            )}

            {/* Expired Prescription - Doctolib Link */}
            {!validityResult.isValid && (() => {
              const birth = parseFrenchDate(formData.birthDate);
              const currentAge = birth
                ? Math.floor((new Date() - birth) / (365.25 * 24 * 60 * 60 * 1000))
                : NaN;
              const { href, label } = doctolibRenewalByAge(
                Number.isFinite(currentAge) ? currentAge : 0
              );
              return (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Prochaine étape :</h3>
                <Alert className="border-rust bg-rust/5">
                  <AlertDescription className="text-sm break-words">
                    Pour pouvoir choisir de nouvelles lunettes, vous devez d'abord renouveler votre ordonnance
                    auprès d'un ophtalmologue.
                  </AlertDescription>
                </Alert>
                <Button
                  asChild
                  className="w-full bg-rust hover:bg-rust-light text-white"
                  size="lg"
                >
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="h-5 w-5" />
                    <span className="break-words">{label}</span>
                  </a>
                </Button>
                <p className="text-xs text-muted-foreground text-center break-words px-4">
                  Une fois votre nouvelle ordonnance obtenue, revenez nous voir pour choisir vos lunettes !
                </p>
              </div>
              );
            })()}

            <Button onClick={() => setStep(1)} variant="outline" className="w-full border-2 border-anthracite text-anthracite hover:bg-anthracite hover:text-white">
              Retour au formulaire
            </Button>
          </div>
        )}

        {/* Step 3: Slot Selection */}
        {step === 3 && (
          <div className="space-y-6 mt-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Choisissez votre créneau</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Sélectionnez un créneau horaire disponible pour votre rendez-vous (durée : 30 minutes)
              </p>
            </div>

            <div style={{width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box'}}>
              <SlotPicker 
                onSlotSelect={handleSlotSelect}
                selectedSlot={selectedSlot}
              />
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="email">Email (optionnel)</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.fr"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Pour recevoir une confirmation par email
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={() => setStep(2)} 
                variant="outline" 
                className="flex-1"
              >
                Retour
              </Button>
              <Button 
                onClick={handleContinueToAppointmentType}
                disabled={!selectedSlot}
                className="flex-1 bg-forest hover:bg-forest-light text-white"
              >
                Continuer
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Appointment Type Selection */}
        {step === 4 && (
          <div className="space-y-6 mt-4">
            <h3 className="text-lg font-semibold">Choisissez le type de rendez-vous :</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => handleAppointmentTypeSelection('Choisir des lunettes')}
                disabled={loading}
                className="appointment-type-btn"
              >
                <div className="appointment-type-content">
                  {loading && selectedType === 'Choisir des lunettes' ? (
                    <Loader2 className="h-10 w-10 animate-spin" />
                  ) : (
                    <Glasses className="h-10 w-10" />
                  )}
                  <span className="appointment-type-title">Choisir des lunettes</span>
                  <span className="appointment-type-desc">
                    Sélection de montures
                  </span>
                </div>
              </button>

              <button
                onClick={() => handleAppointmentTypeSelection('Contrôle de vue')}
                disabled={loading}
                className="appointment-type-btn"
              >
                <div className="appointment-type-content">
                  {loading && selectedType === 'Contrôle de vue' ? (
                    <Loader2 className="h-10 w-10 animate-spin" />
                  ) : (
                    <Eye className="h-10 w-10" />
                  )}
                  <span className="appointment-type-title">Contrôle de vue</span>
                  <span className="appointment-type-desc">
                    Vérification et ajustement
                  </span>
                </div>
              </button>
            </div>

            <Button 
              onClick={() => setStep(3)} 
              variant="outline" 
              className="w-full"
            >
              Retour
            </Button>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <div className="space-y-6 mt-4 text-center py-8">
            <div className="flex justify-center">
              <CheckCircle2 className="h-20 w-20 text-forest" />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-2">Demande de rendez-vous envoyée !</h3>
              <p className="text-muted-foreground">
                Merci {formData.firstName} ! Nous vous contacterons rapidement au {formData.phone} pour
                confirmer votre rendez-vous "{selectedType}".
              </p>
            </div>
            <Button onClick={resetModal} className="bg-forest hover:bg-forest-light" size="lg">
              Fermer
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
